/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  page: 1,
  perPage: 10,
  mode: 'ALL',
  date_start: Func.dateFirstMonth(),
  date_stop: Func.dateEndMonth(),
};

function RpAverageCost() {
  const mod = 'rp_average_cost';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [summary, setSummary] = useState({
    total_cost_receive: 0,
    total_cost_sell: 0,
  });
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [urlExcel, setUrlExcel] = useState('');
  const [urlPrint, setUrlPrint] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const blank = (value) => value === null || value === undefined || value === '';

  const qtyDisplay = (value) => (blank(value) ? '-' : Func.numberDisplay(value));
  const moneyCell = (value) => (blank(value) ? '-' : Func.moneyDisplay(value));

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      mode: data.mode,
      date_start: data.date_start,
      date_stop: data.date_stop,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setSummary(result.summary || { total_cost_receive: 0, total_cost_sell: 0 });
        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('mode', find.mode);
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const pdf = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('mode', find.mode);
    params.append('date_start', find.date_start);
    params.append('date_stop', find.date_stop);

    const url = `${urlPrint}/download?${params.toString()}`;
    window.open(url, '_blank');
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateFormat(start.toDate()),
      date_stop: Func.dateFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        view(find);
        setUrlExcel(result.excel);
        setUrlPrint(result.print);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MDInput
              fullWidth
              value={find.mode}
              select
              onChange={(e) => view({ ...find, mode: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('attr.type')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='SALE'>ขาย</MenuItem>
              <MenuItem value='BUY'>ซื้อ</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={12} md={8} lg={5}>
            <MDDateRange
              dateOnly
              dateStart={find.date_start}
              dateStop={find.date_stop}
              callBack={setDateRange}
              closeDate={() =>
                view({
                  ...find,
                  date_start: Func.dateFirstMonth(),
                  date_stop: Func.dateEndMonth(),
                  page: 1,
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='info' onClick={pdf}>
                <Icon>picture_as_pdf</Icon>&nbsp;PDF
              </MDButton>
              <MDButton variant='gradient' color='success' onClick={excel}>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_account'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table' size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' rowSpan={2}>
                      No.
                    </TableCell>
                    <TableCell rowSpan={2}>SKU</TableCell>
                    <TableCell rowSpan={2}>ชื่อสินค้า</TableCell>
                    <TableCell align='center' rowSpan={2}>
                      วันที่
                    </TableCell>
                    <TableCell align='center' rowSpan={2}>
                      เลขที่ใบรับ
                    </TableCell>
                    <TableCell align='center' colSpan={3}>
                      จัดซื้อ
                    </TableCell>
                    <TableCell align='center' colSpan={3}>
                      ต้นทุนสินค้าที่ขาย
                    </TableCell>
                    <TableCell align='center' colSpan={3}>
                      สินค้าคงเหลือ
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align='right'>ปริมาณ</TableCell>
                    <TableCell align='right'>ราคาต่อหน่วย</TableCell>
                    <TableCell align='right'>ต้นทุนรวม</TableCell>
                    <TableCell align='right'>ปริมาณ</TableCell>
                    <TableCell align='right'>ราคาต่อหน่วย</TableCell>
                    <TableCell align='right'>ต้นทุนรวม</TableCell>
                    <TableCell align='right'>ปริมาณ</TableCell>
                    <TableCell align='right'>ราคาต่อหน่วย</TableCell>
                    <TableCell align='right'>ต้นทุนรวม</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row, i) => (
                    <TableRow
                      key={`${row.barcode_id}-${row.date}-${row.created_at}-${row.quantity_receive}-${row.quantity_sell}-${row.cost_receive}-${row.cost_sell}-${row.total_cost_receive}-${row.total_cost_sell}`}
                      hover
                    >
                      <TableCell align='center'>{(pageNo - 1) * find.perPage + i + 1}</TableCell>
                      <TableCell align='left'>{row.sku}</TableCell>
                      <TableCell align='left'>{row.name}</TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.date)}</TableCell>
                      <TableCell align='center'>{row.receive_code || '-'}</TableCell>
                      <TableCell align='right'>{qtyDisplay(row.quantity_receive)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.cost_receive)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.total_cost_receive)}</TableCell>
                      <TableCell align='right'>{qtyDisplay(row.quantity_sell)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.cost_sell)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.total_cost_sell)}</TableCell>
                      <TableCell align='right'>{qtyDisplay(row.stock_balance)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.cost_fix)}</TableCell>
                      <TableCell align='right'>{moneyCell(row.total_cost)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={7} align='right'>
                      <strong>รวม</strong>
                    </TableCell>
                    <TableCell align='right'>
                      <strong>{Func.moneyDisplay(summary.total_cost_receive)}</strong>
                    </TableCell>
                    <TableCell colSpan={2} />
                    <TableCell align='right'>
                      <strong>{Func.moneyDisplay(summary.total_cost_sell)}</strong>
                    </TableCell>
                    <TableCell colSpan={3} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_account`), t(`menu.rp_average_cost`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpAverageCost;
